import { useEffect } from "react";

import { Link, Navigate, useParams } from "react-router-dom";

import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";

import Card from "../../components/shared/Card/Card";
import { Alert } from "../../components/base/Alert";
import { Button } from "../../components/base/Button";
import { errorResolver } from "../../utils/errorResolver";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomerReadiness from "../../data/CustomerReadiness/useCustomerReadiness";
import {
  IReadinessCheck,
  IReadinessSection,
  ReadinessStatus,
} from "../../models/Customer/CustomerReadiness";

function StatusIcon({ status }: { status: ReadinessStatus }) {
  switch (status) {
    case ReadinessStatus.Pass:
      return <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" />;
    case ReadinessStatus.Fail:
      return <XCircleIcon className="h-5 w-5 shrink-0 text-red-600" />;
    case ReadinessStatus.Warn:
      return (
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-500" />
      );
    default:
      return <MinusCircleIcon className="h-5 w-5 shrink-0 text-gray-300" />;
  }
}

function CheckRow({ check }: { check: IReadinessCheck }) {
  const skipped = check.status === ReadinessStatus.Skip;

  return (
    <li className="flex items-start gap-3 py-2">
      <div className="pt-0.5">
        <StatusIcon status={check.status} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span
            className={
              skipped ? "text-sm text-gray-400" : "text-sm text-gray-900"
            }
          >
            {check.label}
          </span>
          {skipped && (
            <span className="text-xs text-gray-400">not in use</span>
          )}
        </div>
        {check.detail && (
          <p className="mt-0.5 text-xs text-gray-500">{check.detail}</p>
        )}
      </div>
      {check.fixPath && !skipped && (
        <Link
          to={check.fixPath}
          className="shrink-0 text-xs font-medium text-blue-600 hover:text-blue-800"
        >
          Fix
        </Link>
      )}
    </li>
  );
}

function SectionCard({ section }: { section: IReadinessSection }) {
  const complete = section.passed === section.total;

  return (
    <Card
      title={section.label}
      labelId={`readiness-${section.key}`}
      titleRightContent={
        <span
          className={`text-sm font-medium ${
            complete ? "text-green-700" : "text-gray-500"
          }`}
        >
          {section.passed} / {section.total}
        </span>
      }
      bodyClassName="px-4 py-2"
    >
      <p className="pb-2 pt-1 text-xs text-gray-500">{section.description}</p>
      <ul className="divide-y divide-gray-100">
        {section.checks.map((check) => (
          <CheckRow key={check.key} check={check} />
        ))}
      </ul>
    </Card>
  );
}

export function CustomerReadiness() {
  const { customerId } = useParams();
  const { readiness } = useCustomerReadiness(customerId!);
  const { setInfo } = usePageNameContext();

  useEffect(() => {
    const name = readiness.data?.name;
    setInfo({
      name: name ? `${name} readiness` : "Onboarding readiness",
      desc: "What is still missing before this customer can go live",
      pageTitle: name ? `Readiness | ${name}` : "Onboarding readiness",
    });
  }, [readiness.data]);

  if (!customerId) {
    return <Navigate to="/customers" />;
  }

  if (readiness.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <LoadingWheel />
      </div>
    );
  }

  if (readiness.isError || !readiness.data) {
    return (
      <Alert
        severity="error"
        title="Error getting readiness"
        message={errorResolver(readiness.error)}
      />
    );
  }

  const data = readiness.data;
  const outstanding = data.total - data.passed;
  const percent = data.total === 0 ? 100 : Math.round((data.passed / data.total) * 100);

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <Link to={`/customer/${customerId}`}>
          <Button variant="outlined" className="flex items-center space-x-2">
            <ArrowLeftIcon className="-ml-1 h-5 w-5" />
            <p>Back to customer</p>
          </Button>
        </Link>
        <Button
          variant="outlined"
          onClick={() => readiness.refetch()}
          disabled={readiness.isFetching}
        >
          {readiness.isFetching ? "Checking..." : "Re-check"}
        </Button>
      </div>

      <div className="mb-6">
        <Card labelId="readiness-score" bodyClassName="p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <p className="text-3xl font-medium text-gray-900">
                {data.passed} of {data.total} complete
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {outstanding === 0
                  ? "Everything this page can check is done."
                  : `${outstanding} item${outstanding === 1 ? "" : "s"} outstanding.`}
              </p>
            </div>
            <span className="text-sm text-gray-400">
              Checked {new Date(data.generatedAt).toLocaleString()}
            </span>
          </div>

          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className={`h-full rounded-full ${
                outstanding === 0 ? "bg-green-600" : "bg-blue-600"
              }`}
              style={{ width: `${percent}%` }}
            />
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {data.sections.map((section) => (
          <SectionCard key={section.key} section={section} />
        ))}
      </div>

      {data.notChecked.length > 0 && (
        <div className="mt-6">
          <Alert
            severity="info"
            title="Not checked here"
            message={data.notChecked}
          />
        </div>
      )}
    </>
  );
}
